import { Button, Spinner } from "react-bootstrap";
import React, { useState, useEffect, useRef } from "react";
import { api_url } from "../../constant/constant";
import { useTranslation } from "react-i18next";
import { showToast } from "../ShowToast/ToastNotification";
import { useNavigate } from "react-router-dom";
import "./EditCertificateManager.css";

const EditCertificateManager = ({ id }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [backgroundImage, setBackgroundImage] = useState("");

    const [formData, setFormData] = useState({
        title: "",
        html_content: "",
        language: "en",
    });

    const editorRef = useRef(null);
    const [draggedVariable, setDraggedVariable] = useState(null);
    const [customText, setCustomText] = useState("");
    const [fontSize, setFontSize] = useState(16);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    
    // Available variables for drag and drop
    const availableVariables = [
        { name: "{{firstname}}", label: "First Name" },
                { name: "{{fullname}}", label: "Full Name" },

        { name: "{{lastname}}", label: "Last Name" },
        { name: "{{lesson_name}}", label: "Lesson Name" },
        { name: "{{achievement_date}}", label: "Achievement Date" },
        { name: "{{certificate_number}}", label: "Certificate Number" }
    ];

    useEffect(() => {
        fetchCertificateManager();
    }, [id]);

    const fetchCertificateManager = async () => {
        try {
            const token = localStorage.getItem("userToken");
            const response = await fetch(`${api_url}/certificate-templates/${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });
    
            if (response.ok) {
                const result = await response.json();
                console.log("API Response:", result.data);
    
                let imageUrl = "";
                const parser = new DOMParser();
                const doc = parser.parseFromString(result.data.html_content, "text/html");
    
                // Remove only the first <p> tag
                const pTag = doc.querySelector("p");
                if (pTag) {
                    pTag.remove();
                }
    
                // Get updated HTML without first <p> tag
                const cleanedHtml = doc.body.innerHTML;
    
                const div = doc.querySelector("div");
                const bgImage = div?.style?.backgroundImage || "";
    
                if (bgImage.includes("url")) {
                    imageUrl = bgImage.match(/url\(["']?(.*?)["']?\)/)?.[1] || "";
                    console.log("Extracted from HTML:", imageUrl);
                } else {
                    console.log("No background image found in html_content");
                    const bgId = result.data.background_id;
                    if (bgId) {
                        const bgResponse = await fetch(`${api_url}/certificate/get-images/${bgId}`, {
                            method: "GET",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        });
                        const bgResult = await bgResponse.json();
                        if (bgResult?.data?.image_path) {
                            imageUrl = bgResult.data.image_path;
                            console.log("Fetched from API:", imageUrl);
                        }
                    }
                }
    
                setBackgroundImage(imageUrl);
                setFormData({
                    title: result.data.title,
                    html_content: cleanedHtml,
                    language: result.data.language || "en",
                });
                
                // Load existing variables into editor after a short delay to ensure DOM is ready
                setTimeout(() => {
                    loadVariablesIntoEditor(cleanedHtml);
                }, 100);
            } else {
                showToast(t("failedToFetchCertificateData"), "error");
            }
        } catch (error) {
            console.error("Error fetching certificate data:", error);
            showToast(t("errorFetchingCertificateData"), "error");
        }
    };
    

    // Load variables and custom text from existing HTML into editor
    const loadVariablesIntoEditor = (htmlContent) => {
        if (!editorRef.current || !htmlContent) return;
        
        const editor = editorRef.current;
        editor.innerHTML = ""; // Clear editor first
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        
        // Try to find certificate-content div first
        let contentDiv = doc.querySelector('.certificate-content');
        if (!contentDiv) {
            // If not found, try to find the wrapper and get its content
            const wrapper = doc.querySelector('.certificate-wrapper');
            if (wrapper) {
                contentDiv = wrapper.querySelector('div:last-child');
            }
        }
        
        // If still not found, use body content
        if (!contentDiv) {
            contentDiv = doc.body;
        }
        
        const variables = [];
        const customTexts = [];
        
        // Find all spans with certificate-variable class or absolute positioning
        const allSpans = contentDiv.querySelectorAll('span');
        allSpans.forEach((span) => {
            const text = span.textContent.trim();
            const style = span.getAttribute('style') || '';
            const hasAbsolute = style.includes('position:absolute') || style.includes('position: absolute');
            const isVariable = span.classList.contains('certificate-variable');
            const isCustomText = span.classList.contains('certificate-custom-text');
            
            // Extract position from style
            const leftMatch = style.match(/left:\s*([^;]+)/);
            const topMatch = style.match(/top:\s*([^;]+)/);
            const x = leftMatch ? leftMatch[1].trim() : "50";
            const y = topMatch ? topMatch[1].trim() : "50";
            
            if (isVariable) {
                // It's a variable
                const matches = text.match(/\{\{[^}]+\}\}/g);
                if (matches) {
                    matches.forEach(match => {
                        variables.push({
                            name: match,
                            x: x,
                            y: y
                        });
                    });
                }
            } else if (isCustomText || (hasAbsolute && !text.match(/\{\{[^}]+\}\}/g))) {
                // It's custom text (has custom-text class or is absolutely positioned without variables)
                // Extract font styles from the element
                const fontSizeMatch = style.match(/font-size:\s*([^;]+)/);
                const fontWeightMatch = style.match(/font-weight:\s*([^;]+)/);
                const fontStyleMatch = style.match(/font-style:\s*([^;]+)/);
                
                let fontSizeValue = 16;
                if (fontSizeMatch) {
                    const fs = fontSizeMatch[1].trim();
                    fontSizeValue = parseFloat(fs) || 16;
                }
                
                const isBoldValue = fontWeightMatch ? fontWeightMatch[1].trim().toLowerCase().includes('bold') : false;
                const isItalicValue = fontStyleMatch ? fontStyleMatch[1].trim().toLowerCase().includes('italic') : false;
                
                customTexts.push({
                    text: text,
                    x: x,
                    y: y,
                    fontSize: fontSizeValue,
                    bold: isBoldValue,
                    italic: isItalicValue
                });
            } else if (hasAbsolute) {
                // Check if it contains variables
                const matches = text.match(/\{\{[^}]+\}\}/g);
                if (matches) {
                    matches.forEach(match => {
                        variables.push({
                            name: match,
                            x: x,
                            y: y
                        });
                    });
                } else {
                    // No variables, treat as custom text
                    // Extract font styles from the span (we already have the style variable)
                    const fontSizeMatch = style.match(/font-size:\s*([^;]+)/);
                    const fontWeightMatch = style.match(/font-weight:\s*([^;]+)/);
                    const fontStyleMatch = style.match(/font-style:\s*([^;]+)/);
                    
                    let fontSizeValue = 16;
                    if (fontSizeMatch) {
                        const fs = fontSizeMatch[1].trim();
                        fontSizeValue = parseFloat(fs) || 16;
                    }
                    
                    const isBoldValue = fontWeightMatch ? fontWeightMatch[1].trim().toLowerCase().includes('bold') : false;
                    const isItalicValue = fontStyleMatch ? fontStyleMatch[1].trim().toLowerCase().includes('italic') : false;
                    
                    customTexts.push({
                        text: text,
                        x: x,
                        y: y,
                        fontSize: fontSizeValue,
                        bold: isBoldValue,
                        italic: isItalicValue
                    });
                }
            }
        });
        
        // Also search for variables in text nodes that might be in absolutely positioned elements
        const allElements = contentDiv.querySelectorAll('*');
        allElements.forEach((element) => {
            const style = element.getAttribute('style') || '';
            if (style.includes('position:absolute') || style.includes('position: absolute')) {
                const text = element.textContent.trim();
                const matches = text.match(/\{\{[^}]+\}\}/g);
                const leftMatch = style.match(/left:\s*([^;]+)/);
                const topMatch = style.match(/top:\s*([^;]+)/);
                const x = leftMatch ? leftMatch[1].trim() : "50";
                const y = topMatch ? topMatch[1].trim() : "50";
                
                if (matches) {
                    matches.forEach(match => {
                        // Avoid duplicates
                        if (!variables.find(v => v.name === match && v.x === x)) {
                            variables.push({
                                name: match,
                                x: x,
                                y: y
                            });
                        }
                    });
                } else if (!element.classList.contains('certificate-variable') && 
                          !element.classList.contains('certificate-custom-text')) {
                    // Might be custom text
                    if (text && !customTexts.find(ct => ct.text === text && ct.x === x)) {
                        // Extract font styles from the element
                        const elementStyle = element.getAttribute('style') || '';
                        const fontSizeMatch = elementStyle.match(/font-size:\s*([^;]+)/);
                        const fontWeightMatch = elementStyle.match(/font-weight:\s*([^;]+)/);
                        const fontStyleMatch = elementStyle.match(/font-style:\s*([^;]+)/);
                        
                        let fontSizeValue = 16;
                        if (fontSizeMatch) {
                            const fs = fontSizeMatch[1].trim();
                            fontSizeValue = parseFloat(fs) || 16;
                        }
                        
                        const isBoldValue = fontWeightMatch ? fontWeightMatch[1].trim().toLowerCase().includes('bold') : false;
                        const isItalicValue = fontStyleMatch ? fontStyleMatch[1].trim().toLowerCase().includes('italic') : false;
                        
                        customTexts.push({
                            text: text,
                            x: x,
                            y: y,
                            fontSize: fontSizeValue,
                            bold: isBoldValue,
                            italic: isItalicValue
                        });
                    }
                }
            }
        });
        
        // Create variable elements in editor
        variables.forEach((variable) => {
            const variableElement = createVariableElement(variable.name, variable.x, variable.y);
            editor.appendChild(variableElement);
        });
        
        // Create custom text elements in editor
        customTexts.forEach((customTextItem) => {
            const textElement = createTextElement(
                customTextItem.text, 
                customTextItem.x, 
                customTextItem.y,
                customTextItem.fontSize || 16,
                customTextItem.bold || false,
                customTextItem.italic || false
            );
            editor.appendChild(textElement);
        });
    };

    // Create a draggable text element (for custom text)
    const createTextElement = (text, x = null, y = null, fontSizeValue = 16, bold = false, italic = false) => {
        const textElement = document.createElement("span");
        textElement.textContent = text;
        textElement.className = "certificate-custom-text";
        textElement.contentEditable = true;
        textElement.style.position = "absolute";
        
        if (x !== null && y !== null) {
            // Parse x and y values (could be "100px" or "100")
            const xValue = typeof x === 'string' ? parseFloat(x) || 0 : x || 0;
            const yValue = typeof y === 'string' ? parseFloat(y) || 0 : y || 0;
            textElement.style.left = `${xValue}px`;
            textElement.style.top = `${yValue}px`;
        } else {
            textElement.style.left = "50px";
            textElement.style.top = "50px";
        }
        
        textElement.style.cursor = "move";
        textElement.style.padding = "4px 8px";
        textElement.style.backgroundColor = "rgba(0, 123, 255, 0.2)";
        textElement.style.border = "1px dashed #007bff";
        textElement.style.userSelect = "text";
        textElement.style.minWidth = "50px";
        textElement.style.display = "inline-block";
        textElement.style.fontSize = `${fontSizeValue}px`;
        textElement.style.fontWeight = bold ? "bold" : "normal";
        textElement.style.fontStyle = italic ? "italic" : "normal";
        
        // Make the text element draggable within the editor
        let isDragging = false;
        let startX, startY;
        
        textElement.addEventListener("mousedown", (mouseDownEvent) => {
            // Check if user is selecting text (editing mode)
            const selection = window.getSelection();
            if (selection.toString().length > 0) {
                return; // User is selecting text, don't drag
            }
            
            isDragging = true;
            textElement.contentEditable = false;
            startX = mouseDownEvent.clientX - textElement.offsetLeft;
            startY = mouseDownEvent.clientY - textElement.offsetTop;
            textElement.style.cursor = "grabbing";

            const handleMouseMove = (mouseMoveEvent) => {
                if (!isDragging) return;
                
                const newX = mouseMoveEvent.clientX - startX;
                const newY = mouseMoveEvent.clientY - startY;
                
                // Constrain to editor bounds
                const editor = editorRef.current;
                if (editor) {
                    const maxX = editor.offsetWidth - textElement.offsetWidth;
                    const maxY = editor.offsetHeight - textElement.offsetHeight;
                    
                    textElement.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
                    textElement.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
                }
            };

            const handleMouseUp = () => {
                isDragging = false;
                textElement.contentEditable = true;
                textElement.style.cursor = "move";
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        });
        
        // Double-click to remove text element
        textElement.addEventListener("dblclick", (e) => {
            e.stopPropagation();
            if (window.confirm("Remove this text?")) {
                textElement.remove();
            }
        });
        
        return textElement;
    };

    // Create a draggable variable element
    const createVariableElement = (variableName, x = null, y = null) => {
        const variableElement = document.createElement("span");
        variableElement.textContent = variableName;
        variableElement.className = "certificate-variable";
        variableElement.style.position = "absolute";
        
        if (x && y) {
            // Parse x and y values (could be "100px" or "100")
            const xValue = typeof x === 'string' ? parseFloat(x) || 0 : x || 0;
            const yValue = typeof y === 'string' ? parseFloat(y) || 0 : y || 0;
            variableElement.style.left = `${xValue}px`;
            variableElement.style.top = `${yValue}px`;
        } else {
            variableElement.style.left = "50px";
            variableElement.style.top = "50px";
        }
        
        variableElement.style.cursor = "move";
        variableElement.style.padding = "4px 8px";
        variableElement.style.backgroundColor = "rgba(255, 255, 0, 0.3)";
        variableElement.style.border = "1px dashed #333";
        variableElement.style.userSelect = "none";
        variableElement.draggable = true;
        
        // Make the variable element draggable within the editor
        variableElement.addEventListener("mousedown", (mouseDownEvent) => {
            mouseDownEvent.stopPropagation();
            const startX = mouseDownEvent.clientX - variableElement.offsetLeft;
            const startY = mouseDownEvent.clientY - variableElement.offsetTop;

            const handleMouseMove = (mouseMoveEvent) => {
                const newX = mouseMoveEvent.clientX - startX;
                const newY = mouseMoveEvent.clientY - startY;
                
                // Constrain to editor bounds
                const editor = editorRef.current;
                if (editor) {
                    const maxX = editor.offsetWidth - variableElement.offsetWidth;
                    const maxY = editor.offsetHeight - variableElement.offsetHeight;
                    
                    variableElement.style.left = `${Math.max(0, Math.min(newX, maxX))}px`;
                    variableElement.style.top = `${Math.max(0, Math.min(newY, maxY))}px`;
                }
            };

            const handleMouseUp = () => {
                document.removeEventListener("mousemove", handleMouseMove);
                document.removeEventListener("mouseup", handleMouseUp);
            };

            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        });
        
        // Double-click to remove variable
        variableElement.addEventListener("dblclick", (e) => {
            e.stopPropagation();
            if (window.confirm("Remove this variable?")) {
                variableElement.remove();
            }
        });
        
        return variableElement;
    };

    // Handle drag start for variables
    const handleDragStart = (e, variable) => {
        setDraggedVariable(variable);
        e.dataTransfer.effectAllowed = "copy";
    };

    // Handle drag over in editor
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };

    // Handle drop in editor
    const handleDrop = (e) => {
        e.preventDefault();
        if (!draggedVariable || !editorRef.current) return;

        const editor = editorRef.current;
        const rect = editor.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const variableElement = createVariableElement(draggedVariable.name, x, y);
        editor.appendChild(variableElement);
        setDraggedVariable(null);
    };

    // Get HTML content from editor
    const getEditorHTML = () => {
        if (!editorRef.current) return "";
        return editorRef.current.innerHTML;
    };

    // Handle adding custom text
    const handleAddCustomText = () => {
        if (!customText.trim()) {
            showToast("Please enter some text!", "warning");
            return;
        }
        
        if (!editorRef.current) return;
        
        const editor = editorRef.current;
        const rect = editor.getBoundingClientRect();
        const x = rect.width / 2 - 50; // Center horizontally
        const y = rect.height / 2 - 20; // Center vertically
        
        const textElement = createTextElement(customText.trim(), x, y, fontSize, isBold, isItalic);
        editor.appendChild(textElement);
        setCustomText(""); // Clear input after adding
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Helper function to convert image URL to base64
    const imageToBase64 = async (imageUrl) => {
        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error("Error converting image to base64:", error);
            return imageUrl; // Return original URL if conversion fails
        }
    };

    const handleEditCertificateManager = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        // Get HTML content from editor
        const editorHTML = getEditorHTML();
        
        if (!editorHTML.trim()) {
            showToast("Please add at least one variable to the certificate!", "error");
            setIsLoading(false);
            return;
        }
        
        if (!backgroundImage) {
            showToast("Background image is missing!", "warning");
            setIsLoading(false);
            return;
        }

        // Convert image to base64
        let base64Image = backgroundImage;
        try {
            base64Image = await imageToBase64(backgroundImage);
        } catch (error) {
            console.error("Failed to convert image to base64, using original URL:", error);
        }

        // Clean and prepare HTML content for dompdf
        // Extract only the variable elements and their positions
        let cleanedContent = editorHTML;
        
        // Ensure variables have proper styling for dompdf
        cleanedContent = cleanedContent.replace(
            /<span class="certificate-variable"(.*?)>(.*?)<\/span>/g,
            '<span class="certificate-variable"$1 style="position:absolute; margin:0; padding:4px 8px;">$2</span>'
        );
        
        // Ensure custom text elements have proper styling for dompdf
        cleanedContent = cleanedContent.replace(
            /<span class="certificate-custom-text"(.*?)>(.*?)<\/span>/g,
            (match, attrs, content) => {
                // Extract existing style attribute
                const styleMatch = attrs.match(/style=["']([^"']*)["']/);
                let existingStyle = styleMatch ? styleMatch[1] : '';
                
                // Ensure required styles are present
                if (!existingStyle.includes('position:absolute')) {
                    existingStyle = 'position:absolute; ' + existingStyle;
                }
                if (!existingStyle.includes('margin:')) {
                    existingStyle = existingStyle + ' margin:0;';
                }
                if (!existingStyle.includes('padding:')) {
                    existingStyle = existingStyle + ' padding:4px 8px;';
                }
                
                // Remove style attribute from attrs and add the merged style
                const cleanAttrs = attrs.replace(/style=["'][^"']*["']/, '').trim();
                return `<span class="certificate-custom-text"${cleanAttrs} style="${existingStyle}">${content}</span>`;
            }
        );

        // dompdf-compatible HTML structure
        // Using fixed A4 landscape dimensions (1123px x 794px at 96dpi)
        // Using absolutely positioned img tag with base64 source
        const wrappedHtmlContent = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>Certificate</title>
<style>
    @page {
        size: A4 landscape;
        margin: 0;
    }
    * {
        box-sizing: border-box;
    }
    body {
        margin: 0;
        padding: 0;
        width: 1123px;
        height: 794px;
        position: relative;
        overflow: hidden;
    }
    .certificate-wrapper {
        position: absolute;
        top: 0;
        left: 0;
        width: 1123px;
        height: 794px;
        margin: 0;
        padding: 0;
        overflow: hidden;
    }
    .certificate-bg-img {
        position: absolute;
        top: 0;
        left: 0;
        width: 1123px;
        height: 794px;
        z-index: 1;
        object-fit: contain;
        margin: 0;
        padding: 0;
    }
    .certificate-content {
        position: absolute;
        top: 0;
        left: 0;
        width: 1123px;
        height: 794px;
        padding: 0;
        margin: 0;
        z-index: 2;
        overflow: visible;
    }
</style>
</head>
<body>
<div class="certificate-wrapper">
    <img src="${base64Image}" class="certificate-bg-img" alt="Certificate Background" />
    <div class="certificate-content">
        ${cleanedContent}
    </div>
</div>
</body>
</html>`;

        try {
            const token = localStorage.getItem("userToken");
            const response = await fetch(`${api_url}/certificate-templates/${id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...formData,
                    html_content: wrappedHtmlContent,
                }),
            });

            if (response.ok) {
                showToast(t("certificateUpdatedSuccessfully"), "success");
                navigate(-1);
            } else {
                showToast(t("failedToUpdateCertificate"), "error");
            }
        } catch (error) {
            showToast(t("somethingWentWrongRefresh"), "error");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="addCertificateContainer" style={{ marginTop: "20px" }}>
            <form onSubmit={handleEditCertificateManager}>
                <div className="row">
                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">{t("Title")}</label>
                            <input
                                type="text"
                                name="title"
                                className="form-control"
                                onChange={handleChange}
                                value={formData.title}
                                required
                            />
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="mb-3">
                            <label className="form-label">{t("Language")}</label>
                            <select
                                name="language"
                                className="form-select"
                                onChange={handleChange}
                                value={formData.language}
                                required
                            >
                                <option value="en">{t("English")}</option>
                                <option value="ar">{t("Arabic")}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="col-md-8">
                    <div className="mb-3">
                        <label className="form-label mb-2">{t("CertificateDesign") || "Certificate Design"}</label>
                        
                        {/* Variables Panel */}
                        <div className="variables-panel mb-3 p-3" style={{ 
                            border: "1px solid #ddd", 
                            borderRadius: "5px", 
                            backgroundColor: "#f8f9fa" 
                        }}>
                            <label className="form-label mb-2" style={{ fontWeight: "bold" }}>
                                {t("Variables") || "Variables"} - {t("DragAndDrop") || "Drag and drop to add"}
                            </label>
                            <div className="d-flex flex-wrap gap-2">
                                {availableVariables.map((variable, index) => (
                                    <span
                                        key={index}
                                        draggable
                                        onDragStart={(e) => handleDragStart(e, variable)}
                                        className="variable-badge"
                                        style={{
                                            padding: "8px 12px",
                                            backgroundColor: "#6897cb",
                                            color: "white",
                                            borderRadius: "4px",
                                            cursor: "grab",
                                            userSelect: "none",
                                            fontSize: "14px"
                                        }}
                                    >
                                        {variable.name}
                                    </span>
                                ))}
                            </div>
                            <small className="text-muted d-block mt-2">
                                {t("DoubleClickToRemove") || "Double-click on a variable to remove it"}
                            </small>
                        </div>

                        {/* Custom Text Panel */}
                        <div className="custom-text-panel mb-3 p-3" style={{ 
                            border: "1px solid #ddd", 
                            borderRadius: "5px", 
                            backgroundColor: "#f0f8ff" 
                        }}>
                            <label className="form-label mb-2" style={{ fontWeight: "bold" }}>
                                {t("CustomText") || "Custom Text"}
                            </label>
                            <div className="d-flex gap-2 align-items-end mb-2">
                                <div className="flex-grow-1">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder={t("EnterCustomText") || "Enter custom text..."}
                                        value={customText}
                                        onChange={(e) => setCustomText(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                e.preventDefault();
                                                handleAddCustomText();
                                            }
                                        }}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={handleAddCustomText}
                                >
                                    {t("AddText") || "Add Text"}
                                </button>
                            </div>
                            
                            {/* Font Style Controls */}
                            <div className="d-flex gap-2 align-items-center flex-wrap">
                                <div className="d-flex align-items-center gap-2">
                                    <label className="form-label mb-0" style={{ fontSize: "14px" }}>
                                        {t("FontSize") || "Font Size"}:
                                    </label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        style={{ width: "80px" }}
                                        min="8"
                                        max="72"
                                        value={fontSize}
                                        onChange={(e) => setFontSize(parseInt(e.target.value) || 16)}
                                    />
                                    <span style={{ fontSize: "12px" }}>px</span>
                                </div>
                                
                                <div className="d-flex gap-1">
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${isBold ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setIsBold(!isBold)}
                                        title={t("Bold") || "Bold"}
                                    >
                                        <strong>B</strong>
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${isItalic ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setIsItalic(!isItalic)}
                                        title={t("Italic") || "Italic"}
                                    >
                                        <em>I</em>
                                    </button>
                                </div>
                            </div>
                            
                            <small className="text-muted d-block mt-2">
                                {t("CustomTextHint") || "Add text to certificate. Click and drag to move, double-click to edit, double-click again to remove."}
                            </small>
                        </div>

                        {/* Certificate Editor */}
                        <div className="certificate-editor-wrapper" style={{ 
                            border: "2px dashed #ccc", 
                            borderRadius: "5px",
                            position: "relative",
                            overflow: "hidden",
                            width: "1123px",
                            height: "794px",
                            margin: "0 auto"
                        }}>
                            <div
                                ref={editorRef}
                                id="certificateEditor"
                                className="certificate-editor"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                style={{
                                    position: "relative",
                                    width: "1123px",
                                    height: "794px",
                                    backgroundImage: backgroundImage ? `url(${backgroundImage})` : "none",
                                    backgroundSize: "contain",
                                    backgroundPosition: "center center",
                                    backgroundRepeat: "no-repeat"
                                }}
                            />
                        </div>
                        
                        <div className="mt-2">
                            <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                {isLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" role="status" className="me-2" />
                                        {t("Update...")}
                                    </>
                                ) : (
                                    t("UpdateCertificateManager")
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default EditCertificateManager;